import React, { memo, ReactNode, Suspense } from 'react';
import LuxonAdapter from '@date-io/luxon';
// import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
    DateObjectUnits,
    DateTime,
    DateTimeUnit,
    DiffOptions,
    Duration,
    DurationLike,
    DurationUnits,
    Settings,
    ToHumanDurationOptions,
    ToRelativeOptions,
    Zone,
} from 'luxon';
import ToJsDate from 'to-js-date';
import firebase from 'firebase/app';
import isequal from 'lodash/isEqual';
import i18next from 'i18next';
import { Nullable } from '../forms/fields/supplementary/Membertable/util';
import { trans } from '../services/i18n';
import ContentLoader from '../components/ContentLoader';

Settings.defaultZone = 'Europe/Oslo';
Settings.defaultLocale = i18next.language;

export const luxonAdapter = new LuxonAdapter({ locale: i18next.language });

type LuxonUnit =
    | 'years'
    | 'year'
    | 'months'
    | 'month'
    | 'weeks'
    | 'week'
    | 'days'
    | 'day'
    | 'hours'
    | 'hour'
    | 'minutes'
    | 'minute'
    | 'seconds'
    | 'second'
    | 'milliseconds'
    | 'millisecond';

const validUnitKeys = ['year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond'];

const LuxonValidUnitKeys = {
    year: 'year',
    years: 'year',
    month: 'month',
    months: 'month',
    day: 'day',
    days: 'day',
    hour: 'hour',
    hours: 'hour',
    minute: 'minute',
    minutes: 'minute',
    quarter: 'quarter',
    quarters: 'quarter',
    second: 'second',
    seconds: 'second',
    millisecond: 'millisecond',
    milliseconds: 'millisecond',
    weekday: 'weekday',
    weekdays: 'weekday',
    weeknumber: 'weekNumber',
    weeksnumber: 'weekNumber',
    weeknumbers: 'weekNumber',
    weekyear: 'weekYear',
    weekyears: 'weekYear',
    ordinal: 'ordinal',
} as const;
function getValidUnit(unit: Nullable<string | DurationUnits>) {
    if (unit === undefined || unit === null) {
        return undefined;
    }
    return LuxonValidUnitKeys[String(unit).toLowerCase() as keyof typeof LuxonValidUnitKeys];
}

type GetDiffOptions = {
    conversionAccuracy?: 'exact' | 'precise' | 'default';
    decimals?: number;
    round?: 'up' | 'down' | 'default';
};

export class DateFormat {
    private static _timezone: string = Settings.defaultZone.name;
    private static _locale: string = Settings.defaultLocale;

    static readonly DATE_SHORT: Intl.DateTimeFormatOptions = DateTime.DATE_SHORT;
    static readonly DATE_SHORT_2: Intl.DateTimeFormatOptions = {
        ...DateTime.DATE_SHORT,
        hour: '2-digit',
        minute: '2-digit',
    };
    static readonly DATE_MED: Intl.DateTimeFormatOptions = DateTime.DATE_MED;
    static readonly DATE_MED_WITH_WEEKDAY: Intl.DateTimeFormatOptions = DateTime.DATE_MED_WITH_WEEKDAY;
    static readonly DATE_MED_WITHOUT_YEAR: Intl.DateTimeFormatOptions = {
        ...DateTime.DATE_MED,
        year: undefined,
    };
    static readonly DATE_FULL: Intl.DateTimeFormatOptions = DateTime.DATE_FULL;
    static readonly DATE_FULL_WITH_WEEKDAY: Intl.DateTimeFormatOptions = {
        ...DateTime.DATE_FULL,
        weekday: 'long',
    };
    static readonly DATE_HUGE: Intl.DateTimeFormatOptions = DateTime.DATE_HUGE;
    static readonly DATETIME_SHORT: Intl.DateTimeFormatOptions = DateTime.DATETIME_SHORT;
    static readonly DATETIME_SHORT_WITH_SEPARATOR = 'dd.MM.yyyy- HH:mm';
    static readonly DATETIME_SHORT_WITH_SECONDS: Intl.DateTimeFormatOptions = DateTime.DATETIME_SHORT_WITH_SECONDS;
    static readonly DATETIME_MED: Intl.DateTimeFormatOptions = DateTime.DATETIME_MED;
    static readonly DATETIME_MED_WITHOUT_YEAR: Intl.DateTimeFormatOptions = {
        ...DateTime.DATETIME_MED,
        year: undefined,
    };
    static readonly DATETIME_MED_WITH_SECONDS: Intl.DateTimeFormatOptions = DateTime.DATETIME_MED_WITH_SECONDS;
    static readonly DATETIME_MED_WITH_WEEKDAY: Intl.DateTimeFormatOptions = DateTime.DATETIME_MED_WITH_WEEKDAY;
    static readonly DATETIME_FULL: Intl.DateTimeFormatOptions = DateTime.DATETIME_FULL;
    static readonly DATETIME_FULL_WITH_WEEKDAY: Intl.DateTimeFormatOptions = {
        ...DateTime.DATETIME_FULL,
        weekday: 'long',
    };
    static readonly DATETIME_FULL_WITH_SECONDS: Intl.DateTimeFormatOptions = DateTime.DATETIME_FULL_WITH_SECONDS;
    static readonly DATETIME_HUGE: Intl.DateTimeFormatOptions = DateTime.DATETIME_HUGE;
    static readonly DATETIME_HUGE_WITH_SECONDS: Intl.DateTimeFormatOptions = DateTime.DATETIME_HUGE_WITH_SECONDS;
    static readonly YEAR_SHORT: Intl.DateTimeFormatOptions = {
        year: '2-digit',
    };

    static readonly YEAR_LONG: Intl.DateTimeFormatOptions = {
        year: 'numeric',
    };

    static readonly TIME_24_SIMPLE: Intl.DateTimeFormatOptions = DateTime.TIME_24_SIMPLE;
    static readonly TIME_24_WITH_SECONDS: Intl.DateTimeFormatOptions = DateTime.TIME_24_WITH_SECONDS;
    static readonly TIME_24_WITH_SHORT_OFFSET: Intl.DateTimeFormatOptions = DateTime.TIME_24_WITH_SHORT_OFFSET;
    static readonly TIME_24_WITH_LONG_OFFSET: Intl.DateTimeFormatOptions = DateTime.TIME_24_WITH_LONG_OFFSET;
    static readonly TIME_24_WITH_WEEKDAY: Intl.DateTimeFormatOptions = {
        weekday: 'narrow',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h24',
    };
    static readonly TIME_24_WITH_WEEKDAY_SECONDS: Intl.DateTimeFormatOptions = {
        weekday: 'narrow',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h24',
    };
    static readonly TIME_SIMPLE: Intl.DateTimeFormatOptions = DateTime.TIME_SIMPLE;
    static readonly TIME_WITH_SECONDS: Intl.DateTimeFormatOptions = DateTime.TIME_WITH_SECONDS;
    static readonly TIME_WITH_SHORT_OFFSET: Intl.DateTimeFormatOptions = DateTime.TIME_WITH_SHORT_OFFSET;
    static readonly TIME_WITH_LONG_OFFSET: Intl.DateTimeFormatOptions = DateTime.TIME_WITH_LONG_OFFSET;
    static readonly TIME_WITH_WEEKDAY: Intl.DateTimeFormatOptions = {
        weekday: 'narrow',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    };
    static readonly TIME_WITH_SECONDS_WEEKDAY: Intl.DateTimeFormatOptions = {
        weekday: 'narrow',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h12',
    };
    static readonly WEEKDAY_MED: Intl.DateTimeFormatOptions = {
        weekday: 'short',
    };
    static readonly WEEKDAY_MED_WITH_DATETIME: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h24',
    };
    static readonly WEEKDAY_LONG: Intl.DateTimeFormatOptions = {
        weekday: 'long',
    };
    static readonly WEEKDAY_LONG_WITH_DAY: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
    };
    static readonly WEEKDAY_LONG_WITH_DATE: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    };
    static readonly WEEKDAY_LONG_WITH_DATETIME: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h24',
    };
    static readonly DATE: Intl.DateTimeFormatOptions = {
        day: 'numeric',
    };
    static readonly DATE_2: Intl.DateTimeFormatOptions = {
        day: '2-digit',
    };
    static readonly DATE_WITH_TIME_24_SHORT: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h24',
    };
    static readonly DB_DATE = 'yyyy-MM-dd';
    static readonly DB_FULL = 'yyyy-MM-dd HH:mm:ss';
    static readonly DB_DATE_TIME = 'yyyy-MM-dd HH:mm';
    static readonly UNIX_TIMESTAMP_SECONDS = 'X';

    static get locale() {
        return this._locale;
    }
    static set locale(locale: string) {
        this._locale = locale;
    }
    static get timeZone() {
        return this._timezone;
    }
    static set timeZone(timeZone: Zone | string) {
        this._timezone = typeof timeZone === 'object' ? timeZone.name : timeZone;
    }
}

type HumanDiffOptions = ToHumanDurationOptions & {
    breakOff?: number;
    onlyLargest?: boolean;
    showSince?: boolean;
    format?: Intl.DateTimeFormatOptions;
};
const DEFAULT_HUMAN_OPTIONS: HumanDiffOptions = {
    onlyLargest: true,
    breakOff: 25,
    listStyle: 'narrow',
    signDisplay: 'never',
    useGrouping: true,
    maximumFractionDigits: 0,
    format: DateFormat.DATETIME_SHORT,
};
type ValidLuxorConstructorProp =
    | Date
    | string
    | DateTime
    | Luxon
    | number
    | firebase.firestore.Timestamp
    | undefined
    | null;
export class Luxon<
    DateType extends ValidLuxorConstructorProp = ValidLuxorConstructorProp,
    IsValid extends boolean = true,
> {
    /* Static Values */
    private static _locale: string = i18next.language;
    private static _timeZone: string = '';

    public static guessTz(): string {
        if (typeof window !== 'undefined' && typeof window.Intl !== 'undefined') {
            this._timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Oslo';
        }
        return this._timeZone;
    }

    static get locale() {
        return this._locale;
    }
    static get timeZone() {
        return this._timeZone;
    }

    /* Instanced values */
    private readonly _date: Date | undefined;
    private _dateTime: DateTime<IsValid>;

    private initialize(datetime: DateTime<IsValid>) {
        const initialValues = validUnitKeys.reduce((output, key) => {
            let value;
            try {
                value = datetime.get(key as keyof DateTime);
            } catch {
                value = undefined;
            }

            if (typeof value !== 'number') {
                return output;
            }

            return {
                ...(output || {}),
                [key]: value,
            };
        }, {});

        try {
            this.set(initialValues);
        } catch (e) {
            console.error(e);
        }

        return this._dateTime;
    }

    private getAsDateTime(date: unknown) {
        if (date) {
            try {
                if (date instanceof Date) {
                    return DateTime.fromJSDate(date) as DateTime<IsValid>;
                }
                if (typeof date === 'object') {
                    if ('toJsDate' in date) {
                        return DateTime.fromJSDate((date as any).toJsDate() as Date) as DateTime<IsValid>;
                    }
                    if ('toDateTime' in date) {
                        return (date as Luxon).toDateTime() as DateTime<IsValid>;
                    }
                }
                if (date instanceof Luxon) {
                    return date.raw as DateTime<IsValid>;
                }
                if (date instanceof DateTime) {
                    return date as DateTime<IsValid>;
                }

                return DateTime.fromJSDate(ToJsDate(date)) as DateTime<IsValid>;
            } catch {
                try {
                    (date as any)?.getAsDateTime?.();
                } catch {
                    return this._dateTime;
                }
            }
        }
        return DateTime.now() as DateTime<IsValid>;
    }

    constructor(date?: Nullable<DateType>) {
        if (date) {
            this._dateTime = this.getAsDateTime(date);
            this.initialize(this._dateTime);
            try {
                this._date = ToJsDate(date);
            } catch {}
        } else {
            this._dateTime = DateTime.now() as DateTime<IsValid>;
        }
        if (!Luxon._locale) {
            Luxon._locale = i18next.language;
            Settings.defaultLocale = Luxon._locale;
            DateFormat.locale = Luxon._locale;
            this.setLocale(Luxon._locale);
        }

        if (!Luxon._timeZone) {
            Luxon.guessTz();
            Settings.defaultZone = Luxon._timeZone;
            DateFormat.timeZone = Luxon._timeZone;
            this.setZone(Luxon._timeZone);
        }
    }

    get(unit: keyof DateTime) {
        const validUnit = getValidUnit(unit as string);
        if (!validUnit) {
            return undefined;
        }
        return this._dateTime.get(validUnit as keyof DateTime);
    }

    getPossibleOffsets(): this[] {
        return this._dateTime.getPossibleOffsets().map((i: any) => new Luxon(i)) as this[];
    }

    get isValid() {
        return this._dateTime.isValid;
    }

    get invalidReason() {
        return this._dateTime.invalidReason;
    }

    get invalidExplanation() {
        return this._dateTime.invalidExplanation;
    }

    get locale() {
        return this._dateTime.locale;
    }

    get numberingSystem() {
        return this._dateTime.numberingSystem;
    }

    get outputCalendar() {
        return this._dateTime.outputCalendar;
    }

    get zone() {
        return this._dateTime.zone;
    }

    get zoneName() {
        return this._dateTime.zoneName;
    }

    get year() {
        return this._dateTime.year;
    }

    get quarter() {
        return this._dateTime.quarter;
    }

    get month() {
        return this._dateTime.month;
    }

    get day() {
        return this._dateTime.day;
    }

    get hour() {
        return this._dateTime.hour;
    }

    get minute() {
        return this._dateTime.minute;
    }

    get second() {
        return this._dateTime.second;
    }

    get millisecond() {
        return this._dateTime.millisecond;
    }

    get weekYear() {
        return this._dateTime.weekYear;
    }

    get weekNumber() {
        return this._dateTime.weekNumber;
    }

    get weekday() {
        return this._dateTime.weekday;
    }

    get isWeekend() {
        return this._dateTime.weekday === 6 || this._dateTime.weekday === 7;
    }

    get localWeekday() {
        return this._dateTime.weekday;
    }

    get localWeekNumber() {
        return this._dateTime.weekNumber;
    }

    get localWeekYear() {
        return this._dateTime.weekYear;
    }

    get ordinal() {
        return this._dateTime.ordinal;
    }

    get monthShort() {
        return this._dateTime.toFormat('MMM');
    }

    get monthLong() {
        return this._dateTime.toFormat('MMMM');
    }

    get weekdayShort() {
        return this._dateTime.toFormat('EEE');
    }

    get weekdayLong() {
        return this._dateTime.toFormat('EEEE');
    }

    get offset() {
        return this._dateTime.offset;
    }

    get offsetNameShort() {
        return this._dateTime.offsetNameShort;
    }

    get offsetNameLong() {
        return this._dateTime.offsetNameLong;
    }

    get isOffsetFixed() {
        return this._dateTime.isOffsetFixed;
    }

    get isInDST() {
        return this._dateTime.isInDST;
    }

    get isInLeapYear() {
        return this._dateTime.isInLeapYear;
    }

    get daysInMonth() {
        return this._dateTime.daysInMonth;
    }

    get daysInYear() {
        return this._dateTime.daysInYear;
    }

    get weeksInWeekYear() {
        return this._dateTime.weeksInWeekYear;
    }

    get weeksInLocalWeekYear() {
        return this._dateTime.weeksInWeekYear;
    }

    resolvedLocaleOptions(opts?: Intl.DateTimeFormatOptions) {
        return this._dateTime.resolvedLocaleOptions(opts);
    }

    toUTC(offset?: number, opts?: { keepLocalTime?: boolean }) {
        return this._dateTime.toUTC(offset, opts);
    }

    toLocal() {
        return this._dateTime.toLocal();
    }

    setZone(zone?: string, opts?: { keepLocalTime?: boolean }) {
        this._dateTime = this._dateTime.setZone(zone, opts) as DateTime<IsValid>;
        return this;
    }

    reconfigure(properties: { locale?: string; outputCalendar?: string; numberingSystem?: string }) {
        this._dateTime = this._dateTime.reconfigure(properties) as DateTime<IsValid>;
        return this;
    }

    setLocale(locale: string) {
        this._dateTime = this._dateTime.setLocale(locale) as DateTime<IsValid>;
        return this;
    }

    set(values: DateObjectUnits) {
        const validUpdate = Object.entries(values || {}).reduce((out, [key, value]) => {
            const validKey = getValidUnit(key);
            if (!validKey) {
                return out;
            }
            return {
                ...out,
                [validKey]: value,
            };
        }, {} as DateObjectUnits);

        try {
            this._dateTime = this._dateTime.set(validUpdate);
        } catch {}
        return this;
    }

    plus(duration: Duration | DurationLike) {
        return new Luxon(this._dateTime.plus(duration));
    }

    minus(duration: Duration | DurationLike) {
        return new Luxon(this._dateTime.minus(duration));
    }

    startOf(unit: DateTimeUnit) {
        return new Luxon(this._dateTime.startOf(unit));
    }

    endOf(unit: DateTimeUnit) {
        return new Luxon(this._dateTime.endOf(unit));
    }

    toFormat(fmt: string) {
        return this._dateTime.toFormat(fmt);
    }

    toLocaleString(formatOpts?: Intl.DateTimeFormatOptions, opts?: { locale?: string }) {
        return this._dateTime.toLocaleString(formatOpts, opts);
    }

    toLocaleParts(opts?: Intl.DateTimeFormatOptions) {
        return this._dateTime.toLocaleParts(opts);
    }

    toISO(opts?: { includeOffset?: boolean; suppressSeconds?: boolean; suppressMilliseconds?: boolean }) {
        return this._dateTime.toISO(opts);
    }

    toISODate(opts?: { format?: 'basic' | 'extended' }) {
        return this._dateTime.toISODate(opts);
    }

    toISOWeekDate() {
        return this._dateTime.toISOWeekDate();
    }

    toISOTime(opts?: { suppressMilliseconds?: boolean; suppressSeconds?: boolean; includeOffset?: boolean }) {
        return this._dateTime.toISOTime(opts);
    }

    toRFC2822() {
        return this._dateTime.toRFC2822();
    }

    toHTTP() {
        return this._dateTime.toHTTP();
    }

    toSQLDate() {
        return this._dateTime.toSQLDate();
    }

    toSQLTime(opts?: { includeOffset?: boolean }) {
        return this._dateTime.toSQLTime(opts);
    }

    toSQL(opts?: { includeOffset?: boolean }) {
        return this._dateTime.toSQL(opts);
    }

    toString() {
        return this._dateTime.toString();
    }

    valueOf() {
        return this._dateTime.valueOf();
    }

    toMillis() {
        return this._dateTime.toMillis();
    }

    toSeconds() {
        return this._dateTime.toSeconds();
    }

    toUnixInteger() {
        return this._dateTime.toUnixInteger();
    }

    toJSON() {
        return this._dateTime.toJSON();
    }

    toBSON(): Date {
        return this._dateTime.toJSDate();
    }

    toObject(opts?: { includeConfig?: boolean }) {
        return this._dateTime.toObject(opts);
    }

    toJSDate(): Date {
        return this._dateTime.toJSDate();
    }

    humanDiff<Other extends ValidLuxorConstructorProp>(
        otherDate: Other,
        options: HumanDiffOptions = DEFAULT_HUMAN_OPTIONS,
    ) {
        const comp = luxon(otherDate);

        const humanDiff = this.diff(comp, undefined, { onlyLargest: options.onlyLargest ?? true });
        const humanFormat = humanDiff.toHuman(options);
        if (options.showSince !== false) {
            return `${humanFormat} ${trans('common.ago')?.toLowerCase()}`;
        }
        return humanFormat;
    }
    humanDiffNow(options: HumanDiffOptions = DEFAULT_HUMAN_OPTIONS) {
        const difference: Duration = luxon().diff(this._dateTime, 'days');
        if (Math.abs(difference.days) > (options.breakOff ?? 25)) {
            return this.format(options.format ?? DateFormat.DATE_SHORT);
        }

        return luxon().humanDiff(this._dateTime, options);
    }

    diff<Other extends ValidLuxorConstructorProp>(
        otherDateTime: Other,
        unit?: DurationUnits,
        opts?: DiffOptions & { onlyLargest?: boolean },
    ): Duration {
        const comp = luxon(otherDateTime).raw;
        const validUnit = getValidUnit(unit) as DurationUnits;
        const difference = this._dateTime.diff(comp, validUnit ?? 'seconds', opts);
        if (validUnit) {
            return difference;
        }
        const millis = Math.abs(Math.floor(difference.seconds));
        let units: DurationUnits = ['seconds'];
        if (millis > 60) {
            if (opts?.onlyLargest) {
                units = ['minutes'];
            } else {
                units.push('minutes');
            }
        }
        if (millis > 60 * 60) {
            if (opts?.onlyLargest) {
                units = ['hours'];
            } else {
                units.push('hours');
            }
        }
        if (millis > 24 * 60 * 60) {
            if (opts?.onlyLargest) {
                units = ['days'];
            } else {
                units.push('days');
            }
        }
        if (millis > (luxon().get('daysInMonth') ?? 30) * 24 * 60 * 60) {
            if (opts?.onlyLargest) {
                units = ['months'];
            } else {
                units.push('months');
            }
        }
        if (millis > (luxon().get('daysInYear') ?? 365) * 24 * 60 * 60) {
            if (opts?.onlyLargest) {
                units = ['years'];
            } else {
                units.push('years');
            }
        }

        return this._dateTime.diff(comp, units, {
            ...opts,
        });
    }

    getDiff<Other extends ValidLuxorConstructorProp>(
        otherDateTime: Other,
        unit: DurationUnits = 'seconds',
        opts: GetDiffOptions = { conversionAccuracy: 'default', round: 'default' },
    ): number {
        const comp = luxon(otherDateTime).raw;
        const getValue: any = Array.isArray(unit)
            ? unit.map((entry) => getValidUnit(entry))
            : getValidUnit(unit as string);
        let difference;
        switch (opts.conversionAccuracy) {
            case 'exact':
                difference = this._dateTime.diff(comp, unit, { conversionAccuracy: 'longterm' }).get(getValue);
                break;
            case 'precise':
                difference = this._dateTime.diff(comp, unit, { conversionAccuracy: 'casual' }).get(getValue);
                break;
            default:
                difference = this._dateTime.diff(comp, unit).get(getValue);
                break;
        }
        if (!opts?.conversionAccuracy || opts.conversionAccuracy === 'default') {
            switch (opts.round) {
                case 'up':
                    difference = Math.ceil(difference);
                    break;
                case 'down':
                    difference = Math.floor(difference);
                    break;
                default:
                    difference = Math.round(difference);
                    break;
            }
        }
        if (opts.decimals || opts.conversionAccuracy === 'precise') {
            return parseFloat(difference.toFixed(opts.decimals ?? 3));
        }
        return difference;
    }

    diffNow(unit?: DurationUnits, opts?: { conversionAccuracy?: 'longterm' | 'casual' }): Duration {
        return this.diff(DateTime.now(), unit, opts);
    }

    until(otherDateTime: DateTime) {
        return this._dateTime.until(otherDateTime);
    }

    hasSame(otherDateTime: DateTime, unit: DateTimeUnit): boolean {
        const comp = this.getAsDateTime(otherDateTime);
        return this._dateTime.hasSame(comp, unit);
    }

    equals(other: DateTime): boolean {
        return this._dateTime.equals(other);
    }

    toRelative(options?: ToRelativeOptions) {
        return this._dateTime.toRelative(options);
    }

    toRelativeCalendar(options?: { base?: DateTime; locale?: string }) {
        return this._dateTime.toRelativeCalendar(options);
    }

    public add<Unit extends LuxonUnit, Targ extends [DurationLike] | [number | string, Unit]>(
        ...prop: Targ
    ): Luxon<DateType, IsValid> {
        if (prop?.[0] instanceof Duration) {
            return this.plus(prop[0]) as Luxon<DateType, IsValid>;
        }
        if (prop.length === 2) {
            const [increment, unit] = prop as [number | string, LuxonUnit];
            const validUnit = getValidUnit(unit);
            const validIncrement =
                typeof increment === 'string' && /\d+/.test(increment) ? parseFloat(increment) : increment;
            if (!validUnit) {
                return this as Luxon<DateType, IsValid>;
            }
            return this.plus({ [validUnit]: validIncrement }) as Luxon<DateType, IsValid>;
        }
        return this.plus(prop[0]) as Luxon<DateType, IsValid>;
    }

    public subtract<Targ extends [DurationLike] | [number, LuxonUnit]>(...prop: Targ): Luxon<DateType, IsValid> {
        if (prop?.[0] instanceof Duration) {
            return this.minus(prop[0]) as Luxon<DateType, IsValid>;
        }
        if (prop.length === 2) {
            const [increment, unit] = prop as [number | string, LuxonUnit];
            const validUnit = getValidUnit(unit);
            const validIncrement =
                typeof increment === 'string' && /\d+/.test(increment) ? parseFloat(increment) : increment;
            if (!validUnit) {
                return this as Luxon<DateType, IsValid>;
            }
            return this.minus({ [validUnit]: validIncrement }) as Luxon<DateType, IsValid>;
        }
        return this.minus(prop[0]) as Luxon<DateType, IsValid>;
    }

    public isAfter<Date>(date: Date): boolean {
        const comp = this.getAsDateTime(date);
        return luxonAdapter.isAfter(this._dateTime, comp);
    }

    public isBefore<Date>(date: Date): boolean {
        const comp = this.getAsDateTime(date);
        return luxonAdapter.isBefore(this._dateTime, comp);
    }

    public isEqual<Date>(
        date: Date,
        unit?: 'date' | 'year' | 'month' | 'day' | 'time' | 'hour' | 'minute' | 'second',
    ): boolean {
        const comp = this.getAsDateTime(date);
        switch (unit) {
            case 'date':
                return this.isEqual(comp, 'year') && this.isEqual(comp, 'month') && this.isEqual(comp, 'day');
            case 'year':
                return this._dateTime.hasSame(comp, 'year');
            case 'month':
                return this._dateTime.hasSame(comp, 'month');
            case 'day':
                return isequal(this._dateTime.day.valueOf(), comp.day.valueOf());
            case 'time':
                return this.isEqual(comp, 'hour') && this.isEqual(comp, 'minute') && this.isEqual(comp, 'second');
            case 'hour':
                return isequal(this._dateTime.hour.valueOf(), comp.hour.valueOf());
            case 'minute':
                return isequal(this._dateTime.minute.valueOf(), comp.minute.valueOf());
            case 'second':
                return isequal(this._dateTime.second.valueOf(), comp.second.valueOf());
            default:
                return this._dateTime.equals(comp);
        }
    }

    public isSameOrBefore<Date>(date: Date): boolean {
        return this.isBefore(date) || this.isEqual(date);
    }

    public isSameOrAfter<Date>(date: Date): boolean {
        return this.isAfter(date) || this.isEqual(date);
    }

    public format<T extends Intl.DateTimeFormatOptions | string>(format: T): string {
        try {
            let output: string | undefined;
            if (typeof format === 'string') {
                output = this._dateTime.toFormat(format);
                if (output === 'Invalid DateTime' || output === format) {
                    output = luxonAdapter.formatByString(this._dateTime, format);
                    if (output === format) {
                        throw new Error();
                    }
                }
            } else {
                output = this._dateTime.toLocaleString(format);
            }
            return output;
        } catch {
            try {
                if (typeof format === 'string') {
                    return luxonAdapter.format(this._dateTime, format as never);
                }
                return 'Invalid DateTime';
            } catch {
                return 'Invalid DateTime';
            }
        }
    }

    public set tz(zone: string) {
        this.setZone(zone);
    }

    public get tz(): string {
        return this.zoneName || 'Europe/Oslo';
    }

    public clone() {
        return new Luxon(this.toJSDate());
    }

    public toDateTime(): DateTime {
        return this.getAsDateTime(this._dateTime);
    }

    get raw() {
        return this._dateTime;
    }
}

export function luxon<Date extends ValidLuxorConstructorProp, IsValid extends boolean>(
    ...props: [Date] | []
): Luxon<Date, IsValid> {
    if (props.length === 0) {
        return new Luxon();
    }

    return new Luxon(props[0]) as Luxon<Date, IsValid>;
}

type Props<T> = {
    children: T;
};

const LuxonProvider = <T extends ReactNode>({ children }: Props<T>) => {
    return (
        <Suspense fallback={<ContentLoader animate={false} />}>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18next.language}>
                {/*<MuiPickersUtilsProvider utils={LuxonAdapter} locale={i18next.language}>*/}
                {children}
                {/*</MuiPickersUtilsProvider>*/}
            </LocalizationProvider>
        </Suspense>
    );
};

export default memo(LuxonProvider);
